<template>
  <xlsx-workbook>
    <xlsx-sheet
      v-for="sheet in worksheets"
      :key="sheet.name"
      :collection="sheet.data"
      :sheet-name="sheet.name"
    />

    <xlsx-download
      :filename="`${exportName}.xlsx`"
    >
      <button>
        {{ item.text }}
      </button>
    </xlsx-download>
  </xlsx-workbook>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import _ from 'underscore'

import { XlsxDownload, XlsxSheet, XlsxWorkbook } from 'vue-xlsx'

import dates from 'src/utils/dates'
import { fireanalytics } from 'src/store/firebase'

const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'ExportXlsx',
  components: {
    XlsxDownload,
    XlsxSheet,
    XlsxWorkbook,
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    showExportBar: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional columns array with columnValue functions.
     * If provided, overrides currentColumns from store.
     */
    columns: {
      type: Array,
      default: null,
    },
    /**
     * Optional groups array with items.
     * If provided, overrides currentGroups from store.
     */
    groups: {
      type: Array,
      default: null,
    },
    /**
     * Optional report name for the export file.
     * If provided, overrides reportName from store.
     */
    exportReportName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      typeMap: {
        boolean: {
          type: 'b',
        },
        capitalize: {
          type: 's',
        },
        currency: {
          type: 'n',
          format: '$#,##0.00',
        },
        dollars: {
          type: 'n',
          format: '$#,##0.00',
        },
        date: {
          type: 'd',
          format: 'mm/dd/yyyy',
        },
        dateObject: {
          type: 'd',
          format: 'mm/dd/yyyy',
        },
        error: {
          type: 'e',
        },
        hhmm: {
          type: 's',
        },
        join: {
          type: 's',
        },
        number: {
          type: 'n',
          format: '#,###,0',
        },
        percentage: {
          type: 'n',
        },
        discrepancyPercentage: {
          type: 'n',
        },
        text: {
          type: 's',
        },
        stub: {
          type: 'z',
        },
      },
    }
  },
  computed: {
    ...mapToolsGetters([
      'columnsByKeyReducer',
      'currentColumns',
      'currentGroups',
      'reportKey',
      'sortByDirections',
      'sortByKeys',
    ]),
    ...mapToolsState({
      reportName: (state) => state.reportName,
      tableSortMap: (state) => state.tableSortMap,
    }),
    /**
     * Resolved columns - uses prop if provided, otherwise falls back to store
     */
    resolvedColumns() {
      return this.columns || this.currentColumns
    },
    /**
     * Resolved groups - uses prop if provided, otherwise falls back to store
     */
    resolvedGroups() {
      return this.groups || this.currentGroups
    },
    /**
     * Resolved report name - uses prop if provided, otherwise falls back to store
     */
    resolvedReportName() {
      return this.exportReportName || this.reportName
    },
    exportData() {
      const {
        columnsByKeyReducer,
        resolvedColumns: exportColumns,
        resolvedGroups: exportGroups,
        resolvedReportName: exportName,
        showExportBar,
        typeMap,
      } = this

      if (!showExportBar || !exportColumns) return { headers: [], rows: [] }

      const columnsByKey = columnsByKeyReducer(exportColumns)
      const sortedItemsByGroup = exportGroups.reduce((previousValue, currentGroup) => {
        const { items: currentItems, rows: currentRows } = currentGroup
        const unsortedItems = currentItems || currentRows
        const promiseSortedRows = this.sortGroupedItemsByAllColumns({ columnsByKey, unsortedItems })

        return previousValue.concat(promiseSortedRows)
      }, [])
      const reportItems = sortedItemsByGroup.reduce((flatArray, items) => flatArray.concat(items), [])

      const headers = exportColumns.map((column) => column.columnName)
      const getExportAs = (exportAs) => (Array.isArray(exportAs) && exportAs[0]) || exportAs

      const getFormatForFunction = (formatter) => typeof formatter === 'function' && 'text'
      const getFormatForArray = (formatter) => Array.isArray(formatter) && formatter[0]
      const getFormatterType = (formatter) => getFormatForFunction(formatter) || getFormatForArray(formatter) || formatter

      const formatCells = (exportRows, item) => {
        // Helper functions to clean and convert number values.
        const getStrippedValue = (value) => String(value ?? '').replace(/(\$|%|,)/g, '')
        const isValidNumber = (value) => typeof value === 'number' && !Number.isNaN(value)
        const getValidNumber = (value) => (isValidNumber(value) ? value : Number(getStrippedValue(value)))

        // Build each row by formatting each cell based on its column definition.
        const exportRow = exportColumns.reduce((newItem, column) => {
          const { columnName, exportAs, formatAs } = column
          const formatter = getExportAs(exportAs) || formatAs
          const formatterType = getFormatterType(formatter) || 'text'
          const cellMap = typeMap[formatterType] || typeMap.text
          const { type: cellType, format: cellFormat } = cellMap
          const isNumericalCellType = cellType === 'n'
          const numberFormat = isNumericalCellType && cellFormat ? cellFormat : undefined

          // Get the formatted value from the column's columnValue function.
          // Defensive check: use column.key fallback if columnValue is not a function.
          const formattedValue = typeof column.columnValue === 'function'
            ? column.columnValue(item)
            : item[column.key]

          // Handle both number and string values. Convert to number if needed.
          const exportValue = isNumericalCellType ? getValidNumber(formattedValue) : formattedValue

          // Construct the cell object for xlsx export.
          newItem[columnName] = { t: cellType, v: exportValue, z: numberFormat }

          // Return the updated row object.
          return newItem
        }, {})

        exportRows.push(exportRow)

        return exportRows
      }

      const valueGetter = (row) => dates.get(row.date).unix()
      const sortCreativesByDate = () => /creatives_/.test(exportName) && _.sortDscByCaseInsensitive(reportItems, valueGetter)
      const sourceRows = sortCreativesByDate() || reportItems
      const rows = sourceRows.reduce(formatCells, [])

      return { headers, rows }
    },
    exportName() {
      const reportName = this.resolvedReportName
      const exportDate = dates.get().format('YYYY-MM-DD')

      return `oao_${reportName}_on_${exportDate}`
    },
    worksheets() {
      const { exportData } = this
      const reportName = this.resolvedReportName
      const { rows: exportRows } = exportData

      // Excel sheet names cannot exceed 31 characters
      const sheetName = reportName.length > 31 ? reportName.substring(0, 31) : reportName

      return [
        {
          name: sheetName,
          data: exportRows,
        },
      ]
    },
  },
  watch: {
    worksheets() {
      fireanalytics.event('goal_preemption', {
        category: 'Export',
        description: 'Export As XLSX',
        label: `Export As XLSX Generated for Report "${this.reportName}"`,
      })
    },
  },
  methods: {
    sortGroupedItems({ columnKey, columnDirection, columnFormat, items }) {
      const sortDsc = columnDirection === this.tableSortMap.DSC
      const sorter = sortDsc ? _.sortDscByCaseInsensitive : _.sortAscByCaseInsensitive
      const isDateColumn = columnFormat === 'date'

      const valueGetter = (item) => {
        const getValue = () => {
          const value = !isDateColumn && item.normalized ? item.normalized[columnKey] : item[columnKey]
          const sortArrayValues = () => (sortDsc ? value.sort().reverse() : value.sort())
          return Array.isArray(value) ? sortArrayValues()[0] : value?.value || value
        }
        const getDate = (value) => dates.getUnixDate(value)
        return isDateColumn ? getDate(getValue()) : getValue()
      }

      return sorter(items, valueGetter)
    },
    sortGroupedItemsByAllColumns({ columnsByKey, unsortedItems }) {
      const sortItems = (items, columnKey, index) => {
        const columnDirection = this.sortByDirections[index]
        const columnFormat = columnsByKey[columnKey]?.formatAs

        return this.sortGroupedItems({ columnDirection, columnFormat, columnKey, items })
      }

      return this.sortByKeys.reduceRight(sortItems, unsortedItems)
    },
  },
}
</script>
